import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, FlatList, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { apiGet } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';
import InlineConfirm from './InlineConfirm';

interface ControllerRow {
  id: string;
  name: string | null;
  isOnline: boolean;
  bayId: string | null;
  bayName: string | null;
  locationName: string | null;
  assignedBays: { id: string; name: string; type: string }[];
  assignedMachines: { id: string; name: string; locationName: string | null }[];
}

/** Compact controller ID for narrow rows: keeps the RPC- prefix start and the
 *  last 4 so hardware-label matching still works ("RPC-a4f8e2c1…350f"). */
export function shortControllerId(id: string): string {
  return id.length > 18 ? `${id.slice(0, 12)}…${id.slice(-4)}` : id;
}

/** Where a controller is currently in use, excluding the entity being edited. */
function inUseBy(row: ControllerRow, exclude?: { bayId?: string; machineId?: string }): string | null {
  const bay = row.assignedBays.find(b => b.id !== exclude?.bayId);
  if (bay) return row.locationName ? `${bay.name} @ ${row.locationName}` : bay.name;
  const machine = row.assignedMachines.find(m => m.id !== exclude?.machineId);
  if (machine) return machine.locationName ? `${machine.name} (vending) @ ${machine.locationName}` : `${machine.name} (vending)`;
  return null;
}

/** ControllerPicker - dropdown of the owner's claimed controllers for bay and
 *  vending assignment. Custom modal list on BOTH platforms (a native <select>
 *  can't show in-use badges or the reassign confirmation). New hardware is
 *  claimed on the Hardware tab; this picker only offers already-claimed units. */
interface ControllerPickerProps {
  readonly value: string;
  /** needsReassign=true means the owner confirmed moving an in-use controller;
   *  pass `reassign: true` to the backend on save. */
  readonly onChange: (controllerId: string, needsReassign: boolean) => void;
  readonly label?: string;
  /** What the controller is being attached to — used in the confirm copy. */
  readonly targetLabel?: string;
  readonly excludeBayId?: string;
  readonly excludeMachineId?: string;
  /** Round 2: 1.4 boards can serve several pieces of equipment (relay-level ownership) — list in-use boards as
   *  "Also used by …" and pick them without the move confirmation. */
  readonly allowShared?: boolean;
  /** Controllers already on this equipment (hidden from the list). */
  readonly excludeIds?: string[];
  /** Hide the "None" row (the equipment editor removes boards with its own control). */
  readonly hideNone?: boolean;
  readonly placeholder?: string;
}

export default function ControllerPicker({
  value, onChange, label = 'Controller', targetLabel = 'this bay',
  excludeBayId, excludeMachineId, allowShared = false, excludeIds = [], hideNone = false, placeholder,
}: ControllerPickerProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();
  const [modalVisible, setModalVisible] = useState(false);
  const [rows, setRows] = useState<ControllerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pendingSteal, setPendingSteal] = useState<ControllerRow | null>(null);

  const exclude = { bayId: excludeBayId, machineId: excludeMachineId };

  const fetchControllers = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const list = await apiGet<ControllerRow[]>('/api/controllers');
      setRows(list);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchControllers(); }, [fetchControllers]);

  const openModal = () => {
    setPendingSteal(null);
    setModalVisible(true);
    fetchControllers();
  };

  const selectedRow = rows.find(r => r.id === value);
  const triggerText = !value
    ? (placeholder ?? 'None — no controller')
    : selectedRow?.name
      ? `${selectedRow.name} · ${shortControllerId(value)}`
      : shortControllerId(value);

  const pick = (row: ControllerRow | null) => {
    if (!row) {
      onChange('', false);
      setModalVisible(false);
      return;
    }
    if (inUseBy(row, exclude) && !allowShared) {
      setPendingSteal(row);
      return;
    }
    onChange(row.id, false);
    setModalVisible(false);
  };

  // Available first, then in-use; stable name order within each group.
  const sorted = [...rows].filter(r => !excludeIds.includes(r.id)).sort((a, b) => {
    const aUsed = inUseBy(a, exclude) ? 1 : 0;
    const bUsed = inUseBy(b, exclude) ? 1 : 0;
    if (aUsed !== bUsed) return aUsed - bUsed;
    return (a.name || a.id).localeCompare(b.name || b.id);
  });

  return (
    <View style={styles.wrap}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity style={styles.trigger} onPress={openModal} activeOpacity={0.7}>
        <Ionicons name="hardware-chip-outline" size={18} color={colors.textLight} />
        <Text style={styles.triggerText} numberOfLines={1}>{triggerText}</Text>
        <Ionicons name="chevron-down" size={18} color={colors.textLight} />
      </TouchableOpacity>

      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={() => setModalVisible(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setModalVisible(false)}>
          <TouchableOpacity activeOpacity={1} style={[styles.modalContent, { marginBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>{label}</Text>

            {loading ? (
              <ActivityIndicator size="small" color={colors.primary} style={styles.loadingSpinner} />
            ) : loadError ? (
              <View style={styles.errorWrap}>
                <Text style={styles.errorText}>Couldn't load controllers.</Text>
                <TouchableOpacity onPress={fetchControllers}>
                  <Text style={styles.retryText}>Retry</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <FlatList
                data={sorted}
                keyExtractor={item => item.id}
                ListHeaderComponent={hideNone ? null :
                  <TouchableOpacity
                    style={[styles.option, !value && styles.optionSelected]}
                    onPress={() => pick(null)}
                  >
                    <View style={styles.optionMain}>
                      <Text style={[styles.optionTitle, !value && styles.optionTitleSelected]}>None — no controller</Text>
                      <Text style={styles.optionSub}>Unassign this equipment's controller</Text>
                    </View>
                    {!value && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                  </TouchableOpacity>
                }
                ListEmptyComponent={
                  <Text style={styles.emptyText}>
                    No controllers yet. Claim one on the Hardware tab first.
                  </Text>
                }
                renderItem={({ item }) => {
                  const used = inUseBy(item, exclude);
                  const selected = item.id === value;
                  return (
                    <TouchableOpacity
                      style={[styles.option, selected && styles.optionSelected]}
                      onPress={() => pick(item)}
                    >
                      <View style={[styles.onlineDot, { backgroundColor: item.isOnline ? colors.success : colors.border }]} />
                      <View style={styles.optionMain}>
                        <Text style={[styles.optionTitle, selected && styles.optionTitleSelected]} numberOfLines={1}>
                          {item.name || shortControllerId(item.id)}
                        </Text>
                        {item.name ? (
                          <Text style={styles.optionSub} numberOfLines={1}>{shortControllerId(item.id)}</Text>
                        ) : null}
                        {used ? (
                          <View style={styles.usedBadge}>
                            <Ionicons name="link" size={11} color={colors.warning} />
                            <Text style={styles.usedText} numberOfLines={1}>{allowShared ? `Also used by ${used}` : `In use — ${used}`}</Text>
                          </View>
                        ) : null}
                      </View>
                      {selected && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                    </TouchableOpacity>
                  );
                }}
              />
            )}

            <InlineConfirm
              visible={!!pendingSteal}
              variant="warning"
              message={pendingSteal
                ? `This controller is running ${inUseBy(pendingSteal, exclude)}. Move it to ${targetLabel}? The other equipment will lose its controller when you save.`
                : ''}
              confirmLabel="Move here"
              onConfirm={() => {
                if (pendingSteal) onChange(pendingSteal.id, true);
                setPendingSteal(null);
                setModalVisible(false);
              }}
              onCancel={() => setPendingSteal(null)}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  wrap: { marginBottom: spacing.md },
  label: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: c.textSecondary,
    marginBottom: spacing.sm,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md,
    borderWidth: 1.5,
    borderColor: c.border,
    backgroundColor: c.surface,
  },
  triggerText: { fontSize: fontSize.sm, color: c.text, flex: 1 },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: c.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    width: '90%',
    maxWidth: 400,
    maxHeight: '75%',
  },
  modalTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  loadingSpinner: { marginVertical: spacing.lg },
  errorWrap: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.lg },
  errorText: { fontSize: fontSize.sm, color: c.textSecondary },
  retryText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.semibold },
  emptyText: { fontSize: fontSize.sm, color: c.textLight, textAlign: 'center', paddingVertical: spacing.lg },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.md,
    marginBottom: 2,
  },
  optionSelected: { backgroundColor: c.primaryBg },
  optionMain: { flex: 1 },
  optionTitle: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium },
  optionTitleSelected: { color: c.primary, fontWeight: fontWeight.semibold },
  optionSub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },
  onlineDot: { width: 8, height: 8, borderRadius: 4 },
  usedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  usedText: { fontSize: fontSize.xs, color: c.warning, fontWeight: fontWeight.medium },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
